import TreeItem from "@mui/lab/TreeItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@mui/material/Typography";
import TreeView from "@mui/lab/TreeView";
import { styled } from "@mui/material/styles";
import { Badge } from "@mui/material";
import useStore from "../../../store";

const colorValue = [
    { color: "default", value: 1 },
    { color: "secondary", value: 2 },
    { color: "primary", value: 3 },
    { color: "success", value: 4 },
    { color: "info", value: 5 },
    { color: "warning", value: 6 },
    { color: "error", value: 7 },
];

function getHighestValueColor(colors) {
    const matchingColors = colorValue.filter((item) => colors.includes(item.color));

    if (matchingColors.length === 0) {
        return "default";
    }

    let maxColor = matchingColors[0];
    for (let i = 1; i < matchingColors.length; i++) {
        if (matchingColors[i].value > maxColor.value) {
            maxColor = matchingColors[i];
        }
    }

    return maxColor.color;
}

function getChildrenBadges(children, badges = []) {
    children.forEach((item) => {
        if (item.badges) {
            badges.push(...item.badges);
        }
        if (item.children) {
            getChildrenBadges(item.children, badges);
        }
    });
    return badges;
}

function getBadgeProps(item) {
    let count = 0;
    const colors = [];

    if (item.badges) {
        item.badges.forEach((badge) => {
            count++;
            colors.push(badge.color);
        });
    }

    if (item.children) {
        const childrenBadges = getChildrenBadges(item.children);

        childrenBadges.forEach((badge) => {
            count++;
            colors.push(badge.color);
        });
    }

    return {
        color: getHighestValueColor(colors),
        badgeContent: count,
        variant: "dot",
        anchorOrigin: {
            vertical: "top",
            horizontal: "left",
        },
    };
}

const StyledTreeItem = styled(TreeItem)(({ theme }) => ({
    ".root": {
        color: theme.palette.text.secondary,
        "&:focus > $label": {
            background: "none",
        },
        "&:focus > $content $label, &:hover > $content $label, &$selected > $content $label": {
            backgroundColor: "transparent",
        },
        "& svg": {
            marginRight: theme.spacing(4),
        },
    },
    ".content": {
        flexDirection: "row-reverse",
        // color: theme.palette.text.secondary,
        borderTopRightRadius: theme.spacing(2),
        borderBottomRightRadius: theme.spacing(2),
        // paddingRight: theme.spacing(1),
        fontWeight: theme.typography.fontWeightMedium,
        "$expanded > &": {
            fontWeight: theme.typography.fontWeightRegular,
        },
    },
    ".group": {
        marginLeft: theme.spacing(4),
    },
    ".expanded": {},
    ".selected": {},
    ".label": {
        fontWeight: "inherit",
        color: "inherit",
        marginRight: 15,
    },
    ".label-root": {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0.5, 0),
        fontSize: 20,
    },
    ".label-text": {
        fontWeight: "inherit",
        flexGrow: 1,
    },
}));

function CustomTreeItem(props) {
    const {
        labelText, icon, image, children, labelInfo, color, bgColor, handleContextMenu, treeItem, ...other
    } = props;

    return (
        <StyledTreeItem
            onContextMenu={handleContextMenu ? ((e) => (handleContextMenu(e, treeItem))) : undefined}
            label={(
                <div className="label-root">
                    {/* eslint-disable-next-line react/jsx-no-undef */}
                    <Badge
                        {...getBadgeProps(treeItem)}
                    >
                        {icon ? <FontAwesomeIcon icon={icon} fixedWidth />
                            : <img src={image} alt="Über PT.Manager" style={{ width: 20, marginRight: 7 }} />}
                        <Typography variant="body2" className="label-text">
                            {labelText}
                        </Typography>
                    </Badge>
                </div>
            )}
            classes={{
                root: "root",
                content: "content",
                expanded: "expanded",
                selected: "selected",
                group: "group",
                label: "label",
            }}
            {...other}
        >
            {children && children.length > 0 ? (
                children.map((item) => (
                    <CustomTreeItem
                        treeItem={item}
                        nodeId={item.id}
                        key={item.id}
                        labelText={item.display}
                        icon={item.icon}
                        image={item.image}
                        handleContextMenu={handleContextMenu}
                    >
                        {item.children}
                    </CustomTreeItem>
                ))
            ) : (null)}
        </StyledTreeItem>
    );
}

const StyledTreeViewContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 225,
    paddingTop: 10,
    paddingBottom: 10,
    ".root": {
        height: "100%",
        display: "contents",
    },
    ".bottom": {
        paddingTop: 10,
        marginTop: "auto",
    },
});

function findById(tree, nodeId) {
    for (let i = 0; i < tree.length; i++) {
        const node = tree[i];

        if (node.id === nodeId) return node;

        if (node.children) {
            const desiredNode = findById(node.children, nodeId);
            if (desiredNode) return desiredNode;
        }
    }
    return false;
}

function TreeViewMenu(props) {
    const { menu, bottomMenu } = props;

    const setCurrentRoute = useStore((store) => store.app.setCurrentRoute);
    const logout = useStore((store) => store.app.logout);

    const dashboardAddTab = useStore((store) => store.dashboard.addTab);
    const setCurrentTab = useStore((store) => store.dashboard.setCurrentTab);
    const dashboardOpenDialog = useStore((store) => store.dashboard.openDialog);

    const setAddWidgetDialogOpen = useStore((store) => store.dashboard.widgets.setAddWidgetDialogOpen);
    const setNotificationsOpen = useStore((store) => store.notifications.setOpen);
    const setDashboardSelectionOpen = useStore((store) => store.dashboard.setDashboardSelectionOpen);

    const nodeSelected = (object, nodeIds, array) => {
        const node = findById(array === "bottom" ? bottomMenu : menu, nodeIds);

        if (node && !(node.children && node.children.length > 0)) {
            if (node.tab) {
                if (node.dialog) {
                    dashboardOpenDialog({
                        icon: node.tabIcon ? node.tabIcon : node.icon,
                        title: node.tabDisplay ? node.tabDisplay : node.display,
                        plugin: !node.isCore && node.module,
                        component: node.tab,
                        data: node.data ? node.data : null,
                    });
                } else {
                    dashboardAddTab({
                        icon: node.tabIcon ? node.tabIcon : node.icon,
                        title: node.tabDisplay ? node.tabDisplay : node.display,
                        plugin: !node.isCore && node.module,
                        component: node.tab,
                        data: node.data ? node.data : null,
                    });
                }
            } else if (!node.action) {
                setCurrentRoute({
                    route: node.href,
                    title: node.tabDisplay ? node.tabDisplay : node.display,
                    icon: node.icon,
                });
                setCurrentTab(-1);

                window.dispatchEvent(new CustomEvent("reactToAngular", {
                    detail: {
                        action: "openRoute",
                        value: {
                            route: node.href,
                        },
                    },
                }));
            } else if (node.action === "currentTab0") {
                setCurrentTab(0);
            } else if (node.action === "openAbout") {
                window.dispatchEvent(new CustomEvent("reactToAngular", {
                    detail: {
                        action: "openModal",
                        value: {
                            animation: false,
                            templateUrl: "about.html",
                            controller: ["$scope", ($scope) => {
                                // eslint-disable-next-line no-undef
                                $scope.appInfo = _appInfo;
                            }],
                            resolve: {
                                // appInfo: $scope.appInfo,
                            },
                        },
                    },
                }));
            } else if (node.action === "logout") {
                logout();
            } else if (node.action === "addWidget") {
                window.dispatchEvent(new CustomEvent("reactToAngular", {
                    detail: {
                        action: "openRoute",
                        value: {
                            route: "/",
                        },
                    },
                }));
                setAddWidgetDialogOpen(true);
            } else if (node.action === "dashboardSelection") {
                setCurrentTab(0);
                setDashboardSelectionOpen(true);
            } else if (node.action === "openNotifications") {
                setNotificationsOpen(true);
            }
        }
    };

    return (
        <StyledTreeViewContainer>
            <TreeView
                className="root"
                defaultCollapseIcon={<FontAwesomeIcon style={{ fontSize: 10 }} icon="chevron-down" />}
                defaultExpandIcon={<FontAwesomeIcon style={{ fontSize: 10 }} icon="chevron-left" />}
                onNodeSelect={(object, nodeIds) => nodeSelected(object, nodeIds)}
            >
                {menu.map((item) => (
                    <CustomTreeItem treeItem={item} nodeId={item.id} key={item.id} labelText={item.display} icon={item.icon} image={item.image} style={{ backgroundColor: item.backgroundColor }}>{item.children}</CustomTreeItem>
                ))}
            </TreeView>
            <TreeView
                className="bottom"
                defaultCollapseIcon={<FontAwesomeIcon style={{ fontSize: 10 }} icon="chevron-down" />}
                defaultExpandIcon={<FontAwesomeIcon style={{ fontSize: 10 }} icon="chevron-left" />}
                onNodeSelect={(object, nodeIds) => nodeSelected(object, nodeIds, "bottom")}
            >
                {bottomMenu.map((item) => (
                    <CustomTreeItem treeItem={item} nodeId={item.id} key={item.id} labelText={item.display} icon={item.icon} image={item.image} style={{ backgroundColor: item.backgroundColor }}>{item.children}</CustomTreeItem>
                ))}
            </TreeView>
        </StyledTreeViewContainer>
    );
}

export default TreeViewMenu;
